const INTEGER_FIELDS = new Set([
  "mileage", "mileage_current", "km_start", "km_end", "next_km",
  "registration_year", "keys_count", "displacement",
]);
const SERVER_METADATA_FIELDS = new Set(["tenant_id", "company_id", "_id", "owner_site_id", "home_location_id", "site_history", "access_schema_version", "access_history", "access_revision", "effective_permissions", "month_pricing", "handover_document"]);

// API records also contain server ownership metadata. Ordinary form writes
// carry editable data; the server derives ownership from the current session.
export function withoutServerMetadata(value) {
  if (Array.isArray(value)) return value.map(withoutServerMetadata);
  if (value == null || typeof value !== "object") return value;
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !SERVER_METADATA_FIELDS.has(key))
    .map(([key, item]) => [key, withoutServerMetadata(item)]));
}

export function romeDate(value = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(value);
  const part = (type) => parts.find((p) => p.type === type).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function calendarMonthCells(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return [
    ...Array(offset).fill(null),
    ...Array.from({ length: days }, (_, i) => `${String(year).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`),
  ];
}

export function isCivilDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= days[month - 1];
}

export function nonNegativeNumber(value, label, { required = false, integer = false } = {}) {
  if (value == null || (typeof value === "string" && value.trim() === "")) {
    if (required) throw new Error(`Il campo "${label}" è obbligatorio`);
    return null;
  }
  if ((typeof value !== "number" && typeof value !== "string") ||
      (typeof value === "string" && !/^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(value.trim()))) {
    throw new Error(`Il campo "${label}" deve contenere un numero valido`);
  }
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0 || (integer && !Number.isSafeInteger(number))) {
    throw new Error(`Il campo "${label}" deve contenere ${integer ? "un numero intero" : "un numero"} maggiore o uguale a zero`);
  }
  return number;
}

function assertOrdered(payload, first, last, message) {
  if (payload[first] != null && payload[last] != null && payload[first] !== "" && payload[last] !== "" && payload[last] < payload[first]) {
    throw new Error(message);
  }
}

export function normalizeBusinessForm(form, fields, resource) {
  const payload = withoutServerMetadata(form);
  for (const field of fields) {
    const value = form[field.key];
    if (field.type === "number" || field.type === "currency") {
      payload[field.key] = nonNegativeNumber(value, field.label, {
        required: !!field.required, integer: INTEGER_FIELDS.has(field.key),
      });
    } else if (field.type === "date") {
      if (value == null || value === "") {
        if (field.required) throw new Error(`Il campo "${field.label}" è obbligatorio`);
        payload[field.key] = null;
      } else {
        if (!isCivilDate(value)) throw new Error(`Il campo "${field.label}" deve contenere una data valida`);
        payload[field.key] = value;
      }
    } else if (field.required && (value == null || String(value).trim() === "")) {
      throw new Error(`Il campo "${field.label}" è obbligatorio`);
    }
  }
  if (resource === "assignments") {
    assertOrdered(payload, "start_date", "end_date_planned", "La fine prevista non può precedere la data di inizio");
    assertOrdered(payload, "start_date", "end_date_actual", "La riconsegna non può precedere la data di inizio");
    const startKm = nonNegativeNumber(payload.km_start, "Km iniziali", { integer: true });
    const endKm = nonNegativeNumber(payload.km_end, "Km finali", { integer: true });
    assertOrdered({ km_start: startKm, km_end: endKm }, "km_start", "km_end", "I km finali non possono essere inferiori ai km iniziali");
  }
  if (resource === "maintenances") {
    assertOrdered(payload, "opened_date", "closed_date", "La chiusura non può precedere l'apertura della manutenzione");
  }
  if (resource === "deadlines") {
    assertOrdered(payload, "issue_date", "expiry_date", "La scadenza non può precedere la data di emissione");
  }
  if (resource === "fines") {
    assertOrdered(payload, "date", "deadline_payment", "La scadenza del pagamento non può precedere la data della multa");
  }
  return payload;
}

export function assignmentFormPayload(payload, existing) {
  const result = withoutServerMetadata(payload);
  for (const key of ["signature", "signature_start", "photos_start", "photos_end", "quote_id", "company_id", "contract", "history", "vehicle_segments", "rental_history", "current_agreement", "rental_start_date", "delivered_at", "is_archived", "pricing", "km_terms", "mileage_settlement", "finance", "assignee_user_id", "driver_dispatch", "client_signature_records", "client_signer", "dispatch_revision", "operation_confirmed", "access_refresh", "handover_confirmations", "delivery_request_id", "return_request_id", "expected_dispatch_revision"]) delete result[key];
  if (!existing) {
    result.status = "pianificata";
  } else {
    if (existing.status !== "pianificata" && result.vehicle_id !== existing.vehicle_id) {
      throw new Error("Il veicolo non può essere cambiato dopo l'avvio della consegna");
    }
    // Le transizioni vengono registrate dalla procedura di consegna e riconsegna.
    delete result.status;
  }
  return result;
}

export function availableRefuelingCosts(costs, refuelings, vehicleId, currentRefuelingId) {
  if (!vehicleId) return [];
  return costs.filter((cost) => {
    if (cost.vehicle_id !== vehicleId || !["carburante", "ricarica"].includes(cost.category)) return false;
    if (refuelings.some((item) => item.id !== currentRefuelingId && item.cost_id === cost.id)) return false;
    const reverseOwner = refuelings.find((item) => item.id === cost.refueling_id);
    // A present cost_id, including null, takes precedence over an older reverse link.
    return !reverseOwner || Object.hasOwn(reverseOwner, "cost_id") || reverseOwner.id === currentRefuelingId;
  });
}

export function handoverMileage(value, minimum, label) {
  const km = nonNegativeNumber(value, label, { required: true, integer: true });
  const min = nonNegativeNumber(minimum, "Chilometraggio di riferimento", { integer: true });
  if (min != null && km < min) throw new Error(`${label}: il valore non può essere inferiore a ${min} km`);
  return km;
}

// Both pickup and return occupy their civil day, including month boundaries.
export function calendarEventOnDay(event, day) {
  if (!isCivilDate(day) || !isCivilDate(event.date) || event.invalid_dates) return false;
  if (event.type !== "assegnazione") return event.date === day;
  return event.date <= day && (!event.end || (isCivilDate(event.end) && day <= event.end));
}
